import React, { ReactNode } from 'react';

import {
  AccessRequestGetResponse,
  AccessStatus,
  DecisionStatus,
  DecisionType,
  RequestProgress,
} from '@ariksa/jit/api';
import {
  Avatar,
  Box,
  Center,
  Flex,
  HStack,
  Stack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { filter, map, startCase, toUpper } from 'lodash';
import { colorOpacity, customTheme } from 'theme';

import {
  CustomTooltip,
  EllipsisText,
  formatTime,
  formatTooltip,
  PlusCountTag,
  Tag,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { CustomRoleIcon, RoleIcon } from 'components/Icons';
import { StackedCell } from 'components/index';
import {
  getHighPrivilegeColor,
  highPrivilegesConst,
} from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/utils';
import { renderStatus } from 'containers/Setup/CloudAccounts/utils';

dayjs.extend(utc);

// JIT names a custom role once the request is approved.
export const getJitRoleName = (row: AccessRequestGetResponse) =>
  row.role_name || row.response_details?.['custom-role-name'];

export const renderJitPrivilegeTags = (row: AccessRequestGetResponse) =>
  row.privileges?.length ? (
    <Wrap spacing={1}>
      {row.privileges.map(privilege => (
        <WrapItem key={privilege}>
          <Tag
            label={privilege}
            styles={{
              tag: { h: 6, minH: 6 },
              label: { textTransform: 'uppercase' },
            }}
          />
        </WrapItem>
      ))}
    </Wrap>
  ) : (
    '-'
  );

// Tells a custom role from a pre-defined one, as on the request form.
export const renderJitRoleIcon = (row: AccessRequestGetResponse) => (
  <Center boxSize={4} flexShrink={0}>
    {row.access_type === 'Custom' ? <CustomRoleIcon /> : <RoleIcon />}
  </Center>
);

// What the request grants, as chips, over the role that grants it.
export const renderJitPermissionsCell = (row: AccessRequestGetResponse) => {
  const isCustom = row.access_type === 'Custom';
  const roleName =
    getJitRoleName(row) ||
    (row.status?.toLowerCase() === 'pending' ? 'Created on approval' : '-');

  return (
    <StackedCell
      upper={renderJitPrivilegeTags(row)}
      showUpperTooltip={false}
      lower={
        <HStack spacing={1.5}>
          {renderJitRoleIcon(row)}
          <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            {roleName}
          </Box>
        </HStack>
      }
      lowerTooltip={`${isCustom ? 'Custom' : 'Pre-defined'} role: ${roleName}`}
    />
  );
};

const VISIBLE_REQUEST_PERMISSIONS = 3;
const MAX_PERMISSION_LENGTH = 24;
const MAX_VISIBLE_APPROVERS = 4;
const VISIBLE_APPROVERS_WITH_OVERFLOW = 3;
const APPROVER_AVATAR_COLORS = [
  customTheme.colors.orange['300'],
  customTheme.colors.blue['300'],
  customTheme.colors.skyBlue['300'],
  customTheme.colors.yellow['300'],
  customTheme.colors.green['300'],
  customTheme.colors.purple['300'],
];

// Exported so the Data Group drawer shows approvers with the same initials.
export const getApproverInitials = (name: string) => {
  const nameParts = name
    .trim()
    .replace(/@.*$/, '')
    .split(/[\s._-]+/)
    .filter(Boolean);

  return nameParts.length > 1
    ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
    : nameParts[0]?.slice(0, 2).toUpperCase();
};

// Exported so the Data Group drawer reuses the same avatar colors.
export const getApproverAvatarColor = (name: string) => {
  const colorIndex = Array.from(name).reduce(
    (total, character) => total + character.charCodeAt(0),
    0,
  );

  return APPROVER_AVATAR_COLORS[colorIndex % APPROVER_AVATAR_COLORS.length];
};

export const renderJitApprovers = (approvers: string[] = []) => {
  const names = approvers.filter(Boolean);
  const visibleApproverCount =
    names.length > MAX_VISIBLE_APPROVERS
      ? VISIBLE_APPROVERS_WITH_OVERFLOW
      : MAX_VISIBLE_APPROVERS;
  const visibleNames = names.slice(0, visibleApproverCount);
  const remainingNames = names.slice(visibleApproverCount);

  if (!names.length) return '—';

  return (
    <HStack spacing={1}>
      {visibleNames.map((name, index) => (
        <CustomTooltip key={`${name}-${index}`} label={name}>
          <Avatar
            name={getApproverInitials(name)}
            size="sm"
            bg={getApproverAvatarColor(name)}
            color="white"
            aria-label={name}
          />
        </CustomTooltip>
      ))}
      {remainingNames.length > 0 && (
        <PlusCountTag
          count={remainingNames.length}
          tooltip={formatTooltip({
            header: 'Approvers',
            content: (
              <Stack spacing={0}>
                {remainingNames.map((name, index) => (
                  <Box key={`${name}-${index}`}>{name}</Box>
                ))}
              </Stack>
            ),
            width: 64,
          })}
        />
      )}
    </HStack>
  );
};

export const renderJitRequestPermissions = row => {
  const permissions = row?.privileges;
  const hasLargePermission = permissions?.some(
    permission => permission?.length > MAX_PERMISSION_LENGTH,
  );
  const visiblePermissionCount = hasLargePermission
    ? 1
    : VISIBLE_REQUEST_PERMISSIONS;
  const visiblePermissions = permissions?.slice(0, visiblePermissionCount);
  const remainingPermissions = permissions?.slice(visiblePermissionCount);

  return (
    <StackedCell
      upper={
        !!permissions?.length ? (
          <HStack spacing={1} minW={0} maxW="full" overflow="hidden">
            {visiblePermissions?.map((permission, index) => (
              <Tag
                key={`${permission}-${index}`}
                label={
                  <EllipsisText
                    label={permission}
                    maxW="180px"
                    tooltip={permission}
                  />
                }
                styles={{
                  tag: {
                    bg: colorOpacity(customTheme.colors.blue['300'], 0.1),
                    border: 'none',
                    flexShrink: 0,
                    maxW: '200px',
                    minW: 0,
                  },
                  label: {
                    color: customTheme.colors.blue['300'],
                    maxW: '180px',
                    minW: 0,
                    textTransform: 'uppercase',
                  },
                }}
              />
            ))}
            {remainingPermissions?.length > 0 && (
              <PlusCountTag
                count={remainingPermissions.length}
                flexShrink={0}
                tooltip={formatTooltip({
                  header: 'Permissions',
                  content: (
                    <Stack spacing={1}>
                      {remainingPermissions.map((permission, index) => (
                        <Box key={`${permission}-${index}`}>{permission}</Box>
                      ))}
                    </Stack>
                  ),
                  width: 64,
                })}
              />
            )}
          </HStack>
        ) : (
          '-'
        )
      }
      lower={
        row?.access_type === 'Custom' ? 'Custom Role' : row?.role_name ?? '-'
      }
      showUpperTooltip={false}
    />
  );
};

export const getCloudUser = (request, requestAccount) => {
  switch (request.cloud_type) {
    case 'AWS':
      return requestAccount.aws_username;
    case 'GCP':
      return `user:${request.requester_email}`;
    default:
      return '-';
  }
};

export const renderJitPermissions = crud_counts => (
  <Wrap>
    {filter(highPrivilegesConst, o => !!crud_counts?.[o]).map((o, index) => {
      return (
        <WrapItem key={index + '-jit-privileges'}>
          <Flex
            px={2}
            bg={getHighPrivilegeColor(o?.toLowerCase())}
            color="white"
            borderRadius={3}
          >
            {o} - {formatNumber(crud_counts?.[o] ?? 0)}
          </Flex>
        </WrapItem>
      );
    })}
  </Wrap>
);

export const renderJitResourceTypes = resources => (
  <HStack spacing={1}>
    {map(resources, (r, index) => (
      <Box key={index + '-jit-resource-type'}>
        {r.resource_type?.toUpperCase()}
        {index + 1 < resources?.length && ','}
      </Box>
    ))}
  </HStack>
);

export const renderJitPermissionsTable = ({ resources, height = '200px' }) => (
  <Box h={height} overflow="scroll">
    <Table
      data={resources}
      columns={[
        {
          header: 'Type',
          accessor: 'resource_type',
          align: 'left',
          render: ({ value }) => toUpper(value),
        },
        {
          header: 'Resource',
          accessor: 'resource_arn',
          align: 'left',
        },
        {
          header: 'Actions',
          accessor: 'iam_actions',
          align: 'left',
          render: ({ value }) => (
            <CustomTooltip
              label={formatTooltip({
                header: 'Actions',
                content: (
                  <Stack>
                    {map(value, (o, index) => (
                      <Box>
                        {index + 1}. {o}
                      </Box>
                    ))}
                  </Stack>
                ),
                width: 72,
              })}
            >
              <HStack spacing={0}>
                {value?.[0] && <Box>{value?.[0]}</Box>}
                {value?.length > 1 && (
                  <>
                    <Box>,</Box>
                    <Tag
                      label={'+' + (value?.length - 1)}
                      styles={{
                        tag: { bg: 'primary' },
                        label: { color: 'white' },
                      }}
                    />
                  </>
                )}
              </HStack>
            </CustomTooltip>
          ),
        },
      ]}
    />
  </Box>
);

export const jitCaptionStyles = {
  fontSize: '13px',
  color: customTheme.colors.gray['250'],
};

export const renderJitSection = (title: ReactNode, content: ReactNode) => (
  <Stack spacing={2}>
    <Box
      fontSize="12px"
      fontWeight={500}
      letterSpacing="0.08em"
      textTransform="uppercase"
      color={customTheme.colors.gray['270']}
    >
      {title}
    </Box>
    {content}
  </Stack>
);

/** A reason beside its author's avatar: who and when, then what they said. */
export const renderJitReasonRow = (
  avatar: ReactNode,
  name: string | undefined,
  details: ReactNode,
  text?: string | null,
  emptyText?: string,
) => (
  <HStack align="start" spacing={2.5}>
    {avatar}
    <Stack spacing={1} flex={1} minW={0}>
      <Box {...jitCaptionStyles}>
        {name && (
          <>
            <Box as="span" color="black" fontWeight={500}>
              {name}
            </Box>
            {' · '}
          </>
        )}
        {details}
      </Box>
      {text ? (
        <Box
          bg={customTheme.colors.gray['50']}
          borderRadius={8}
          px={3}
          py={2.5}
        >
          {text}
        </Box>
      ) : (
        emptyText && <Box {...jitCaptionStyles}>{emptyText}</Box>
      )}
    </Stack>
  </HStack>
);

/** The approvers line under the decision input, leaving out the viewer. */
export const getJitOtherApproversLine = (
  approvers: string[] = [],
  viewerEmail?: string,
) => {
  const viewer = viewerEmail?.toLowerCase();
  const others = approvers.filter(email => email.toLowerCase() !== viewer);
  if (!others.length) return '';
  const names = others.join(', ');
  if (others.length === approvers.length) return `Approvers: ${names}`;
  return others.length === 1
    ? `Also an approver: ${names}`
    : `Other approvers: ${names}`;
};

const requestSourceLabels = { slack: 'Slack', ui: 'UI', api: 'API' };

export const getJitRequestSourceLabel = (source: string) =>
  requestSourceLabels[source] ?? source;

export const formatHoursMinutes = (seconds: number) => {
  const total = Math.max(0, Math.round(seconds / 60));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (!hours) return `${minutes}m`;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

// Request timestamps arrive without a zone, and `Date.parse` would read those
// as local time and land hours out. Parsed as UTC, which is what they are, and
// what the rest of the app assumes.
const secondsUntil = (time?: string | null) =>
  time ? dayjs.utc(time).diff(dayjs.utc(), 'second') : null;

/** The line under the decision: where this request's access window stands. */
const accessLine = (row: AccessRequestGetResponse) => {
  switch (row.access_status) {
    case AccessStatus.NotActive: {
      // Approved and waiting counts down to its start; still pending has no
      // start yet, so the requested window's length is all there is to show.
      if (row.decision_status !== DecisionStatus.Approved) {
        return row.duration_seconds
          ? `Duration ${formatHoursMinutes(row.duration_seconds)}`
          : '';
      }
      const starts = secondsUntil(row.approved_start_time);
      return starts === null ? '' : `Starts ${formatHoursMinutes(starts)}`;
    }
    case AccessStatus.Active: {
      const expires = secondsUntil(row.approved_end_time);
      return expires === null ? '' : `Expires ${formatHoursMinutes(expires)}`;
    }
    case AccessStatus.Expired:
      return 'Expired';
    default:
      return '';
  }
};

export interface JitWindowTile {
  label: string;
  value: string;
  note?: string;
  /** Counting down, so the modal highlights it. */
  isLive?: boolean;
}

/** The last window box: a countdown while one applies, else the length. */
const getJitCountdownTile = (row: AccessRequestGetResponse): JitWindowTile => {
  const duration = row.duration_seconds
    ? formatHoursMinutes(row.duration_seconds)
    : '-';
  const expires = secondsUntil(row.approved_end_time);
  if (row.access_status === AccessStatus.Active && expires !== null) {
    return {
      label: 'Time left',
      value: formatHoursMinutes(expires),
      note: `of ${duration}`,
      isLive: true,
    };
  }
  const starts = secondsUntil(row.approved_start_time);
  if (
    row.access_status === AccessStatus.NotActive &&
    row.decision_status === DecisionStatus.Approved &&
    starts !== null
  ) {
    return {
      label: 'Starts in',
      value: formatHoursMinutes(starts),
      note: `for ${duration}`,
      isLive: true,
    };
  }
  const expiredNote =
    row.progress === RequestProgress.NotGranted ? 'Not granted' : 'Expired';
  return {
    label: 'Duration',
    value: duration,
    note: row.access_status === AccessStatus.Expired ? expiredNote : undefined,
  };
};

const getJitTimeTile = (label: string, time: string | null) => ({
  label,
  value: formatTime(time, 'hh:mm A'),
  note: formatTime(time, 'DD-MMM-YY'),
});

/** The modal's access window: start, end, then a countdown or the length. */
export const getJitWindowTiles = (
  row: AccessRequestGetResponse,
): JitWindowTile[] => {
  // Read from progress: EXPIRED access also covers a request nobody decided
  // and one whose access was never granted.
  const hasEnded = ([
    RequestProgress.CleanupPending,
    RequestProgress.Ended,
  ] as RequestProgress[]).includes(row.progress);
  const hasStarted = hasEnded || row.progress === RequestProgress.Active;
  return [
    getJitTimeTile(
      hasStarted ? 'Started' : 'Starts',
      row.approved_start_time ?? row.requested_start_time,
    ),
    getJitTimeTile(
      hasEnded ? 'Ended' : 'Ends',
      row.approved_end_time ?? row.requested_end_time,
    ),
    getJitCountdownTile(row),
  ];
};

export const renderJitRequestStatus = ({ row }, pendingLabel?: string) => {
  const lower = accessLine(row);
  const isPending = row.decision_status === DecisionStatus.Pending;
  return (
    <StackedCell
      upper={
        isPending && pendingLabel ? (
          // Its own tag rather than the shared renderer, which paints every
          // label black and uppercases it.
          <Tag
            label={pendingLabel}
            styles={{
              tag: { bg: customTheme.colors.yellow['50'], py: 0.5 },
              label: { color: customTheme.colors.orange['400'] },
            }}
          />
        ) : (
          renderStatus({ value: row.decision_status, row })
        )
      }
      lower={lower}
      // Both halves are short and complete, so neither needs a hover copy of
      // itself; the upper one is a tag rather than text.
      showUpperTooltip={false}
      showLowerTooltip={false}
      styles={{
        lower: {
          // Only a line carrying a time is highlighted, so a countdown reads
          // as something moving and `Expired` does not.
          color:
            lower && lower !== 'Expired'
              ? customTheme.colors.primary
              : customTheme.colors.gray['250'],
        },
      }}
    />
  );
};

export const jitDecisionColors = {
  [DecisionStatus.Pending]: {
    bg: customTheme.colors.yellow['50'],
    color: customTheme.colors.orange['400'],
  },
  [DecisionStatus.Approved]: {
    bg: colorOpacity(customTheme.colors.green['300'], 0.14),
    color: customTheme.colors.green['400'],
  },
  [DecisionStatus.Denied]: {
    bg: colorOpacity(customTheme.colors.critical, 0.14),
    color: customTheme.colors.critical,
  },
};

/** The decision as a title-case tag, tinted by outcome. */
export const renderJitDecisionTag = (
  row: AccessRequestGetResponse,
  pendingLabel = 'Pending',
) => {
  const { bg, color } = jitDecisionColors[row.decision_status];
  return (
    <Tag
      label={
        row.decision_status === DecisionStatus.Pending
          ? pendingLabel
          : startCase(row.decision_status.toLowerCase())
      }
      styles={{
        tag: { bg, border: 'none', py: 0.5 },
        label: { color, fontWeight: 'bold' },
      }}
    />
  );
};

/** Initials from a display name, or from the part of an email before `@`. */
export const getJitInitials = (name: string) =>
  name
    .split('@')[0]
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();

export interface JitDecisionEntry {
  name?: string;
  comment?: string;
  isAuto?: boolean;
}

/**
 * Who decided the request, each with their comment. `approved_by` is empty
 * for an auto-approval, whose comment is kept under `system`. The list
 * response does not say who denied a request, or why.
 */
export const getJitDecisionEntries = (
  row: AccessRequestGetResponse,
): JitDecisionEntry[] => {
  if (row.decision_type === DecisionType.Auto) {
    return [
      {
        name: 'Auto-approval policy',
        comment: row.approver_comments?.system,
        isAuto: true,
      },
    ];
  }
  const names = (row.approved_by ?? []).filter(Boolean) as string[];
  if (!names.length) return [{}];
  return names.map(name => ({ name, comment: row.approver_comments?.[name] }));
};

// Filters on the decision, which is what the status column's tag shows.
// Worded neutrally because one dropdown serves both views, where an undecided
// request reads as 'Pending' to its requester and 'For Review' to its approver.
export const jitStatusOptions = [
  { label: 'All', value: undefined },
  ...map(
    [DecisionStatus.Pending, DecisionStatus.Approved, DecisionStatus.Denied],
    o => ({
      label: startCase(o.toLowerCase()),
      value: o,
    }),
  ),
];

export const jitPendingStatusOption = jitStatusOptions.find(
  o => o.value === DecisionStatus.Pending,
)!;
